import { createFeatureSelector, createReducer, createSelector, on } from "@ngrx/store";
import * as authActions from "./auth.actions";
import { AuthStatus, IClientInfo, ILoginSuccessInfo } from "../../models/authentication";
import { UserPermissions } from "../../_helpers/permissions.enum";
import { jwtDecode } from "jwt-decode";
import { IUserInfo } from "../../models/user";
import { UpdateUserSuccessAction } from "../user/user.actions";

export enum OTPLoginStatus {
    NA,
    GenerateOTP,
    GenerateOTPSuccess,
    GenerateOTPFailure,
    VerifyOTP,
    VeriftyOTPSuccess,
    VerifyOTPFailure
}

export interface IAuthState {
    AuthStatus: AuthStatus
    AccessToken: string | null
    AccessTokenExpiry: string | null
    RefreshToken: string | null
    ClientInfo: IClientInfo | null
    UserInfo: IUserInfo | null
    OTPLoginStatus: OTPLoginStatus,
    Permissions: string[]
}

export const initialState: IAuthState = {
    AuthStatus: AuthStatus.LoggedOut,
    AccessToken: null,
    AccessTokenExpiry: null,
    RefreshToken: null,
    UserInfo: null,
    ClientInfo: null,
    OTPLoginStatus: OTPLoginStatus.NA,
    Permissions: []
}

export const authReducer = createReducer<IAuthState>(
    initialState,
    on(authActions.LoginSuccessFulAction, (state: IAuthState, props: { loginSuccessInfo: ILoginSuccessInfo }) => {
        let permissions = parseJwt(props.loginSuccessInfo.AccessToken)?.UserInfo?.Roles.map((x: any) => x.Permissions).flat() || []
        if (permissions.filter((t: string) => t == "*").length > 0) {
            permissions = Object.values(UserPermissions)
        }
        return {
            ...state,
            AuthStatus: AuthStatus.LoggedIn,
            AccessToken: props.loginSuccessInfo.AccessToken,
            RefreshToken: props.loginSuccessInfo.RefreshToken,
            AccessTokenExpiry: props.loginSuccessInfo.Expiry,
            Permissions: permissions
        };
    }),
    on(authActions.LogoutAction, (state: IAuthState) => {
        return {
            ...state,
            AuthStatus: AuthStatus.LoggedOut,
            AccessToken: null,
            AccessTokenExpiry: null,
            RefreshToken: null,
            TempToken: null,
            TempTokenExpiry: null,
            Permissions: []
        }
    }),
    // OTP Status
    on(
        authActions.GenerateOTPAction,
        authActions.GenerateOTPSuccessAction,
        authActions.GenerateOTPFailureAction,
        authActions.VerifyOTPAction,
        authActions.VerifyOTPSuccessAction,
        authActions.VerifyOTPFailureAction,
        (state: IAuthState, { type }) => {
            let updatedOTPStatus: OTPLoginStatus;

            switch (type) {
                case authActions.GenerateOTPAction.type:
                    updatedOTPStatus = OTPLoginStatus.GenerateOTP;
                    break;
                case authActions.GenerateOTPSuccessAction.type:
                    updatedOTPStatus = OTPLoginStatus.GenerateOTPSuccess;
                    break;
                case authActions.GenerateOTPFailureAction.type:
                    updatedOTPStatus = OTPLoginStatus.GenerateOTPFailure;
                    break;
                case authActions.VerifyOTPAction.type:
                    updatedOTPStatus = OTPLoginStatus.VerifyOTP;
                    break;
                case authActions.VerifyOTPSuccessAction.type:
                    updatedOTPStatus = OTPLoginStatus.VeriftyOTPSuccess;
                    break;
                case authActions.VerifyOTPFailureAction.type:
                    updatedOTPStatus = OTPLoginStatus.VerifyOTPFailure;
                    break;
                default:
                    updatedOTPStatus = state.OTPLoginStatus;
            }
            return {
                ...state,
                OTPLoginStatus: updatedOTPStatus,
            };
        }
    ),
    on(authActions.FetchClientInfoSuccessAction, (state: IAuthState, props: { clientInfo: IClientInfo }) => {
        return {
            ...state,
            ClientInfo: props.clientInfo
        };
    }),
    on(UpdateUserSuccessAction, (state, { response }) => {
        return {
            ...state,
            UserInfo: response.Value
        }
    })
);

const selectAuthState = createFeatureSelector<IAuthState>("auth");

export const selectAuthStatus = createSelector(
    selectAuthState,
    (state: IAuthState) => state.AuthStatus
)

// Return Auth states: LoggedIn, LoggedOut, Error, UnRegisteredUser
export const selectActualAuthStatus = createSelector(
    selectAuthState,
    (state: IAuthState) => {
        const token = state.AccessToken ? jwtDecode(state.AccessToken) : null;
        const userInfo = state.UserInfo ? state.UserInfo : token ? (token as any).UserInfo as IUserInfo : null;
        
        if (!token || !userInfo) {
            return state.AuthStatus;
        }
        const isUnRegisteredUser = !(userInfo.Fullname && userInfo.Email && userInfo.Declarations?.IsAbove18);
        return state.AuthStatus == AuthStatus.LoggedIn && isUnRegisteredUser ? AuthStatus.UnRegisteredUser : state.AuthStatus;
    }
)

export const selectClientInfo = createSelector(
    selectAuthState,
    (state: IAuthState) => state.ClientInfo
)

export const selectOTPLoginStatus = createSelector(
    selectAuthState,
    (state: IAuthState) => state.OTPLoginStatus
)

export const selectRefreshToken = createSelector(
    selectAuthState,
    (state: IAuthState) => state.RefreshToken
)

export const selectPermissions = createSelector(
    selectAuthState,
    (state: IAuthState) => state.Permissions
)


//helper methods
function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}